import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import FilterSelect from '../components/FilterSelect';
import { toast } from 'react-toastify';

const GENDER_OPTIONS = [
  { value: '', label: 'Select Gender' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
];

function UserProfile() {
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [form, setForm] = useState({
    name: '',
    gender: '',
    email: '',
    phone_number: '',
    address: '',
    age: '',
    height: '',
    weight: '',
    dateofbirth: '',
    disease_description: '',
  });
  const [medicalRecordFile, setMedicalRecordFile] = useState(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const session = await api.get('user/session-check/');
      const uid = session.data.user_id;
      setUserId(uid);

      const res = await api.get(`user/profile/${uid}/`);
      setProfile(res.data);
      setImagePreview(null); // Reset preview when profile is loaded

      // Set form with all fields from API response
      setForm({
        name: res.data.name || '',
        gender: res.data.gender || '',
        phone_number: res.data.phone_number || '',
        address: res.data.address || '',
        email: res.data.email || '',
        age: res.data.age || '',
        height: res.data.height || '',
        weight: res.data.weight || '',
        dateofbirth: res.data.dateofbirth || '',
        disease_description: res.data.disease_description || '',
      });
      setMedicalRecordFile(null);
    } catch (err) {
      console.error("Error loading profile:", err);
      if (err.response?.status === 401) {
        navigate('/user/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setSaving(true);
      
      // Verify current session and get fresh user_id
      const sessionRes = await api.get('user/session-check/');
      if (!sessionRes.data.authenticated) {
        toast.error('Session expired. Please login again.', {
          position: "bottom-right",
          autoClose: 3000,
        });
        navigate('/user/login');
        return;
      }
      
      const currentUserId = sessionRes.data.user_id;
      
      // Convert form data to FormData for backend compatibility
      // Backend uses MultiPartParser and FormParser, so we need FormData
      const formData = new FormData();
      Object.keys(form).forEach(key => {
        if (form[key] !== null && form[key] !== undefined && form[key] !== '') {
          formData.append(key, form[key]);
        }
      });
      if (medicalRecordFile) {
        formData.append('medical_record', medicalRecordFile);
      }
      
      console.log(`Updating profile for user ID: ${currentUserId}`);
      
      // Send FormData - use api instance
      // Backend expects multipart/form-data (MultiPartParser, FormParser)
      await api.patch(
        `user/profile/${currentUserId}/update/`,
        formData,
        {
          // Don't set Content-Type - axios will automatically set multipart/form-data with boundary
        }
      );
      
      setEditMode(false);
      setImagePreview(null);
      await loadProfile();
      toast.success('Profile updated successfully!', {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Error updating profile:", err);
      
      if (err.response?.status === 403) {
        const errorDetail = err.response?.data?.detail || err.response?.data?.error;
        toast.error(`Access denied: ${errorDetail || 'You can only update your own profile.'}`, {
          position: "bottom-right",
          autoClose: 3000,
        });
        // Refresh session and reload
        await loadProfile();
      } else if (err.response?.data) {
        console.error("Error details:", err.response.data);
        const errorMsg = err.response.data?.error || 
                        err.response.data?.msg || 
                        (typeof err.response.data === 'object' ? JSON.stringify(err.response.data) : err.response.data) ||
                        'Failed to update profile. Please try again.';
        toast.error(`Failed to update profile: ${errorMsg}`, {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        toast.error('Failed to update profile. Please try again.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original profile data
    if (profile) {
      setForm({
        name: profile.name || '',
        gender: profile.gender || '',
        phone_number: profile.phone_number || '',
        address: profile.address || '',
        email: profile.email || '',
        age: profile.age || '',
        height: profile.height || '',
        weight: profile.weight || '',
        dateofbirth: profile.dateofbirth || '',
        disease_description: profile.disease_description || '',
      });
    }
    setMedicalRecordFile(null);
    setEditMode(false);
  };

  const getMedicalRecordUrl = (record) => {
    if (!record) return null;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    if (String(record).startsWith('http')) return record;
    if (String(record).startsWith('/media/')) return `${apiUrl}${record}`;
    return `${apiUrl}/media/${record}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch {
      return dateString;
    }
  };

  // Helper function to get user image URL
  const getUserImageUrl = (image) => {
    if (!image) return null;
    
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    
    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }
    
    return `${apiUrl}/media/${image}`;
  };

  // Handle image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Upload image immediately
      handleImageUpload(file);
    }
  };

  // Upload image to backend
  const handleImageUpload = async (file) => {
    try {
      setUploadingImage(true);
      
      // Verify current session and get fresh user_id
      const sessionRes = await api.get('user/session-check/');
      if (!sessionRes.data.authenticated) {
        toast.error('Session expired. Please login again.', {
          position: "bottom-right",
          autoClose: 3000,
        });
        navigate('/user/login');
        return;
      }
      
      const currentUserId = sessionRes.data.user_id;
      
      const formData = new FormData();
      formData.append('image', file);
      
      console.log(`Uploading image for user ID: ${currentUserId}`);
      
      await api.patch(
        `user/profile/${currentUserId}/update/`,
        formData
      );
      
      // Reload profile to get updated image
      await loadProfile();
      toast.success('Profile image updated successfully!', {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Error uploading image:", err);
      setImagePreview(null);
      
      if (err.response?.status === 403) {
        const errorDetail = err.response?.data?.detail || err.response?.data?.error;
        toast.error(`Access denied: ${errorDetail || 'You can only update your own profile.'}`, {
          position: "bottom-right",
          autoClose: 3000,
        });
        await loadProfile();
      } else {
        const errorMsg = err.response?.data?.error || 
                         err.response?.data?.msg || 
                         'Failed to update image. Please try again.';
        toast.error(errorMsg, {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <>
        <UserNavbar />
        <div className="min-vh-100 d-flex align-items-center justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <UserNavbar />

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            {/* Profile Header Card */}
            <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
              <div className="card-body bg-primary text-white p-4 p-md-5">
                <div className="row align-items-center">
                  <div className="col-auto">
                    <div className="position-relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                        id="user-profile-image-input"
                        disabled={uploadingImage}
                      />
                      {(imagePreview || profile?.image) ? (
                        <img
                          src={imagePreview || getUserImageUrl(profile?.image)}
                          alt={profile?.name || 'User'}
                          className="rounded-circle border border-3 border-white"
                          style={{ width: '120px', height: '120px', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => document.getElementById('user-profile-image-input').click()}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextElementSibling;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <div 
                          className="rounded-circle border border-3 border-white d-flex align-items-center justify-content-center bg-white bg-opacity-10"
                          style={{ 
                            width: '120px', 
                            height: '120px',
                            cursor: 'pointer'
                          }}
                          onClick={() => document.getElementById('user-profile-image-input').click()}
                        >
                          <i className="bi bi-person-circle text-white" style={{ fontSize: '80px' }}></i>
                        </div>
                      )}
                      {uploadingImage ? (
                        <div className="position-absolute top-50 start-50 translate-middle">
                          <div className="spinner-border spinner-border-sm text-white" role="status">
                            <span className="visually-hidden">Uploading...</span>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="position-absolute bottom-0 end-0 bg-white text-primary rounded-circle d-flex align-items-center justify-content-center"
                          style={{ 
                            width: '36px', 
                            height: '36px', 
                            cursor: 'pointer',
                            border: '3px solid #0d6efd'
                          }}
                          onClick={() => document.getElementById('user-profile-image-input').click()}
                          title="Update profile image"
                        >
                          <i className="bi bi-camera-fill" style={{ fontSize: '16px' }}></i>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col">
                    <h2 className="fw-bold mb-2 text-white">{profile?.name || 'User'}</h2>
                    <p className="mb-1 text-white-50">
                      <i className="bi bi-envelope me-2"></i>{profile?.email || 'No email'}
                    </p>
                    {profile?.phone_number && (
                      <p className="mb-0 text-white-50">
                        <i className="bi bi-telephone me-2"></i>{String(profile.phone_number).slice(0, 10)}
                      </p>
                    )}
                  </div>
                  <div className="col-auto">
                    {!editMode && (
                      <button
                        className="btn btn-light rounded-pill px-4 shadow-sm"
                        onClick={() => setEditMode(true)}
                      >
                        <i className="bi bi-pencil me-2"></i>Edit Profile
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Details Card */}
            <div className="card shadow-sm border-0 rounded-4">
              <div className="card-body p-4 p-md-5">

                {!editMode ? (
                  // Display Mode
                  <div className="row g-4">
                    <div className="col-12">
                      <h5 className="fw-bold mb-4 text-primary">
                        <i className="bi bi-info-circle me-2"></i>Personal Information
                      </h5>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="mb-4 p-3 bg-light rounded-3">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-2">
                          <i className="bi bi-person me-2"></i>Name
                        </label>
                        <p className="mb-0 fw-medium fs-5">{profile?.name || '—'}</p>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-4 p-3 bg-light rounded-3">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-2">
                          <i className="bi bi-envelope me-2"></i>Email
                        </label>
                        <p className="mb-0 fw-medium fs-5">{profile?.email || '—'}</p>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-4 p-3 bg-light rounded-3">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-2">
                          <i className="bi bi-gender-ambiguous me-2"></i>Gender
                        </label>
                        <p className="mb-0 fw-medium">{profile?.gender || '—'}</p>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-4 p-3 bg-light rounded-3">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-2">
                          <i className="bi bi-telephone me-2"></i>Phone Number
                        </label>
                        <p className="mb-0 fw-medium">
                          {profile?.phone_number ? String(profile.phone_number).slice(0, 10) : '—'}
                        </p>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="mb-4 p-3 bg-light rounded-3">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-2">
                          <i className="bi bi-geo-alt me-2"></i>Address
                        </label>
                        <p className="mb-0 fw-medium">{profile?.address || '—'}</p>
                      </div>
                    </div>

                    <div className="col-12">
                      <h5 className="fw-bold mb-4 text-primary mt-4">
                        <i className="bi bi-heart-pulse me-2"></i>Health Information
                      </h5>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-4 p-3 bg-light rounded-3">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-2">
                          <i className="bi bi-calendar3 me-2"></i>Age
                        </label>
                        <p className="mb-0 fw-medium">{profile?.age ? `${profile.age} years` : '—'}</p>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-4 p-3 bg-light rounded-3">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-2">
                          <i className="bi bi-rulers me-2"></i>Height
                        </label>
                        <p className="mb-0 fw-medium">{profile?.height ? `${profile.height} cm` : '—'}</p>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-4 p-3 bg-light rounded-3">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-2">
                          <i className="bi bi-speedometer2 me-2"></i>Weight
                        </label>
                        <p className="mb-0 fw-medium">{profile?.weight ? `${profile.weight} kg` : '—'}</p>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-4 p-3 bg-light rounded-3">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-2">
                          <i className="bi bi-calendar-event me-2"></i>Date of Birth
                        </label>
                        <p className="mb-0 fw-medium">{formatDate(profile?.dateofbirth)}</p>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="mb-4 p-3 bg-light rounded-3">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-2">
                          <i className="bi bi-file-medical me-2"></i>Health summary
                        </label>
                        <p className="mb-0 fw-medium">{profile?.disease_description || '—'}</p>
                        {profile?.medical_record && (
                          <a
                            href={getMedicalRecordUrl(profile.medical_record)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary rounded-pill mt-2"
                          >
                            View medical record file
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Edit Mode
                  <div>
                    <h5 className="fw-bold mb-4 text-primary">
                      <i className="bi bi-pencil-square me-2"></i>Edit Profile Information
                    </h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-bold">
                            <i className="bi bi-person me-2 text-primary"></i>Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control rounded-3"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Enter your name"
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-bold">
                            <i className="bi bi-envelope me-2 text-primary"></i>Email <span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-control rounded-3"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder="Enter your email"
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-bold">
                            <i className="bi bi-gender-ambiguous me-2 text-primary"></i>Gender
                          </label>
                          <FilterSelect
                            value={form.gender}
                            onChange={(gender) => setForm({ ...form, gender })}
                            options={GENDER_OPTIONS}
                            placeholder="Select Gender"
                            aria-label="Gender"
                            variant="bordered"
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-bold">
                            <i className="bi bi-telephone me-2 text-primary"></i>Phone Number
                          </label>
                          <input
                            type="tel"
                            className="form-control rounded-3"
                            value={form.phone_number}
                            onChange={(e) => {
                              // Only allow digits and limit to 10 digits
                              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                              setForm({ ...form, phone_number: value });
                            }}
                            placeholder="Enter 10 digit phone number"
                            maxLength={10}
                            pattern="[0-9]{10}"
                            inputMode="numeric"
                          />
                          <small className="text-muted">Enter exactly 10 digits</small>
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label fw-bold">
                            <i className="bi bi-geo-alt me-2 text-primary"></i>Address
                          </label>
                          <textarea
                            className="form-control rounded-3"
                            rows="3"
                            value={form.address}
                            onChange={(e) => setForm({ ...form, address: e.target.value })}
                            placeholder="Enter your address"
                          />
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label fw-bold">
                            <i className="bi bi-calendar3 me-2 text-primary"></i>Age
                          </label>
                          <input
                            type="number"
                            className="form-control rounded-3"
                            value={form.age}
                            onChange={(e) => setForm({ ...form, age: e.target.value })}
                            placeholder="Enter age"
                            min="0"
                          />
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label fw-bold">
                            <i className="bi bi-rulers me-2 text-primary"></i>Height (cm)
                          </label>
                          <input
                            type="number"
                            className="form-control rounded-3"
                            value={form.height}
                            onChange={(e) => setForm({ ...form, height: e.target.value })}
                            placeholder="Height in cm"
                            min="0"
                            step="0.1"
                          />
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label fw-bold">
                            <i className="bi bi-speedometer2 me-2 text-primary"></i>Weight (kg)
                          </label>
                          <input
                            type="number"
                            className="form-control rounded-3"
                            value={form.weight}
                            onChange={(e) => setForm({ ...form, weight: e.target.value })}
                            placeholder="Weight in kg"
                            min="0"
                            step="0.1"
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-bold">
                            <i className="bi bi-calendar-event me-2 text-primary"></i>Date of Birth
                          </label>
                          <input
                            type="date"
                            className="form-control rounded-3"
                            value={form.dateofbirth}
                            onChange={(e) => setForm({ ...form, dateofbirth: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="col-12">
                        <h6 className="fw-bold text-primary mt-2">
                          <i className="bi bi-file-medical me-2" />
                          Medical records
                        </h6>
                      </div>
                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label fw-bold">Health conditions / summary</label>
                          <textarea
                            className="form-control rounded-3"
                            rows={3}
                            value={form.disease_description}
                            onChange={(e) => setForm({ ...form, disease_description: e.target.value })}
                            placeholder="Allergies, chronic conditions, etc."
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label fw-bold">Medical record file</label>
                          {profile?.medical_record && !medicalRecordFile && (
                            <p className="small text-muted mb-2">
                              Current file on profile. Upload a new file to replace.
                            </p>
                          )}
                          <input
                            type="file"
                            className="form-control rounded-3"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setMedicalRecordFile(e.target.files[0] || null)}
                          />
                        </div>
                      </div>

                      <div className="col-12 mt-4 pt-3 border-top">
                        <div className="d-flex gap-2 flex-wrap">
                          <button 
                            className="btn btn-primary px-4 rounded-pill shadow-sm" 
                            onClick={handleUpdate}
                            disabled={saving}
                          >
                            {saving ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-circle me-2"></i>Save Changes
                              </>
                            )}
                          </button>
                          <button
                            className="btn btn-outline-secondary px-4 rounded-pill"
                            onClick={handleCancel}
                            disabled={saving}
                          >
                            <i className="bi bi-x-circle me-2"></i>Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UserProfile;